import React, { useEffect, useState } from "react";
import * as api from "../../api/user-app-api";

export const PrivacyPolicyScreen = () => {
    const [contactNumber, setContactNumber] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadSettings = async () => {
            try {
                const res = await api.fetchSettings();
                setContactNumber(res?.data?.contact_number || "");
            } catch (err) {
                console.error("Failed to load contact details for privacy policy:", err);
            } finally {
                setLoading(false);
            }
        };

        loadSettings();
    }, []);

    return (
        <div className="p-4 pt-0 mb-20">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-5">
                <div>
                    <h2 className="text-base font-bold text-gray-900 mb-2">Privacy Policy</h2>
                    <p className="text-sm text-gray-600">
                        We value your trust and are committed to protecting your personal information.
                        This Privacy Policy explains what data we collect, why we collect it, how we use
                        it, how long we keep it, and what controls you have over your information when
                        using our services.
                    </p>
                </div>

                <div>
                    <h3 className="font-semibold text-gray-800 mb-2">User Data Collection</h3>
                    <p className="text-sm text-gray-600">
                        We collect information that is necessary to create and manage your account, process
                        pickup requests, and provide customer support. This may include your full name,
                        mobile number, email address (if provided), pickup addresses, and communication
                        details submitted through contact forms.
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                        We may also collect service-related information such as order preferences, selected
                        product categories, order timings, and status history to improve service quality,
                        reduce pickup delays, and provide a better user experience.
                    </p>
                </div>

                <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Phone Number Usage</h3>
                    <p className="text-sm text-gray-600">
                        Your phone number is used as a primary identifier for secure login through OTP
                        verification. It is also used to send service-related updates such as order
                        confirmation, pickup scheduling, status updates, and support callbacks.
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                        We do not use your phone number for unrelated spam communication. Any outreach is
                        limited to account, order, compliance, support, or service quality purposes.
                    </p>
                </div>

                <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Order Information Storage</h3>
                    <p className="text-sm text-gray-600">
                        We store order records including selected products, quantities, rates at order time,
                        pickup route, scheduled date, pickup address, and final status (such as Pending,
                        Scheduled, Picked Up, or Cancelled). These records are required for order tracking,
                        invoice generation, operational planning, and customer support.
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                        Historical order information may be retained for business reporting, dispute
                        resolution, fraud prevention, and legal or accounting requirements where applicable.
                    </p>
                </div>

                <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Data Security</h3>
                    <p className="text-sm text-gray-600">
                        We apply reasonable technical and administrative safeguards to protect your data
                        against unauthorized access, misuse, alteration, or disclosure. Access to sensitive
                        information is restricted to authorized personnel and service functions only.
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                        While we work continuously to maintain strong security controls, no system can be
                        guaranteed to be fully immune from risk. Users are encouraged to keep their devices
                        secure and not share OTP codes or login credentials with others.
                    </p>
                </div>

                <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Data Sharing and Third Parties</h3>
                    <p className="text-sm text-gray-600">
                        We do not sell personal data. Information may be shared only when necessary to
                        deliver services, such as operational coordination, customer support handling, legal
                        compliance, payment or invoicing workflows, and platform maintenance.
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                        Any service provider access is limited to the minimum information required to
                        perform assigned work and is expected to follow confidentiality and data protection
                        obligations.
                    </p>
                </div>

                <div>
                    <h3 className="font-semibold text-gray-800 mb-2">User Rights and Choices</h3>
                    <p className="text-sm text-gray-600">
                        You may review and update your profile information in the app where available. You
                        can also contact support for corrections, account-related help, or privacy concerns.
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                        By continuing to use the service, you consent to data practices described in this
                        policy. If policy terms are updated, revised information will apply from the date of
                        publication in the app.
                    </p>
                </div>

                <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Customer Contact Details</h3>
                    {loading ? (
                        <p className="text-sm text-gray-500">Loading contact details...</p>
                    ) : (
                        <p className="text-sm text-gray-600">
                            For privacy-related questions, data correction requests, complaint escalation,
                            or account support, contact us at{" "}
                            <span className="font-semibold text-[#01a499]">
                                {contactNumber || "Not available"}
                            </span>.
                            <br />
                            This contact detail is managed by Admin from the panel and may be updated from
                            time to time.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};
